import React, { useEffect, useRef, useState } from 'react';
import Table, { Column, TableHandle } from '../../../components/Table'; // Adjust path
import { Button } from '../../../components/Button';
import Modal from '../../../components/Modal'; // Adjust path
import { apiGet, apiPost, apiPut, apiDelete } from '../../../services/apiServices';
import { endPoints } from '../../../configs/apiUrls';
import { toast } from 'react-toastify';
import moment from 'moment';

const ConstructionServicesAdmin = () => {
  const tableRef = useRef<TableHandle>(null);

  const [servicesList, setServicesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({ page: 1, limit: 10, search: "" });

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formValues, setFormValues] = useState({
    _id: "",
    title: "",
    priceText: "",
    isActive: true,
  });

  // --- Fetch Services ---
  const fetchServices = async () => {
    setLoading(true);
    try {
      const query = `?page=${filterData.page}&limit=${filterData.limit}&search=${filterData.search}`;
      const res: any = await apiGet(`${endPoints.GET_ALL_CONSTRUCTION_SERVICES}${query}`);
      if (res.success) {
        setServicesList(res.data);
        setTotalCount(res.total);
      }
    } catch (error) {
      toast.error("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [filterData.page, filterData.limit, filterData.search]);

  // --- Handle Modal Open ---
  const openModal = (service: any = null) => {
    if (service) {
      setIsEditing(true);
      setFormValues({
        _id: service._id,
        title: service.title,
        priceText: service.priceText || "",
        isActive: service.isActive,
      });
    } else {
      setIsEditing(false);
      setFormValues({ _id: "", title: "", priceText: "", isActive: true });
    }
    setIsModalOpen(true);
  };

  // --- Submit Create / Edit ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValues.title) return toast.warning("Title is required");

    setSubmitting(true);
    try {
      const payload = {
        title: formValues.title,
        priceText: formValues.priceText,
        isActive: formValues.isActive,
      };

      let res: any;
      if (isEditing) {
        res = await apiPut(endPoints.UPDATE_CONSTRUCTION_SERVICE(formValues._id), payload);
      } else {
        res = await apiPost(endPoints.CREATE_CONSTRUCTION_SERVICE, payload);
      }

      if (res.success) {
        toast.success(isEditing ? "Service updated" : "Service created");
        setIsModalOpen(false);
        fetchServices();
      }
    } catch (error) {
      toast.error("Failed to save service");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Delete Service ---
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    
    try {
      const res: any = await apiDelete(endPoints.DELETE_CONSTRUCTION_SERVICE(id));
      if (res.success) {
        toast.success("Service deleted");
        fetchServices();
      }
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  // --- Table Columns ---
  const columns: Column<any>[] = [
    {
      key: "title",
      label: "Service Title",
      customCell: (row) => <span className="font-semibold text-gray-800">{row.title}</span>
    },
    {
      key: "priceText",
      label: "Pricing Text",
      customCell: (row) => <span className="text-gray-600">{row.priceText || "N/A"}</span>
    },
    {
      key: "isActive",
      label: "Status",
      customCell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${row.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      key: "createdAt",
      label: "Created On",
      customCell: (row) => <span className="text-sm text-gray-500">{moment(row.createdAt).format("DD MMM YYYY")}</span>
    },
    {
      key: "action",
      label: "Actions",
      sortable: false,
      customCell: (row) => (
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => openModal(row)}>Edit</Button>
          <Button variant="error" onClick={() => handleDelete(row._id)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Construction Services</h2>
        <Button variant="primary" onClick={() => openModal()}>+ Add New Service</Button>
      </div>

      <Table
        ref={tableRef}
        columns={columns}
        data={servicesList}
        tableName="Construction Services"
        totalCount={totalCount}
        currentPage={filterData.page}
        pageSize={filterData.limit}
        onSearchChange={(search) => setFilterData({ ...filterData, search, page: 1 })}
        onPageChange={(page) => setFilterData({ ...filterData, page })}
        onPageSizeChange={(limit) => setFilterData({ ...filterData, limit, page: 1 })}
      />

      {/* --- ADD / EDIT MODAL --- */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={isEditing ? "Edit Service" : "Add New Service"}
      >
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-4">
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Title *</label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Residential Building Construction"
              value={formValues.title}
              onChange={(e) => setFormValues({ ...formValues, title: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Text (Optional)</label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Starts at Rs.1999 per sft"
              value={formValues.priceText}
              onChange={(e) => setFormValues({ ...formValues, priceText: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2 mt-2">
            <input 
              type="checkbox" 
              id="isActiveToggle"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={formValues.isActive}
              onChange={(e) => setFormValues({ ...formValues, isActive: e.target.checked })}
            />
            <label htmlFor="isActiveToggle" className="text-sm font-medium text-gray-700">
              Active (Visible on Mobile App)
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isEditing ? "Update Service" : "Create Service"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ConstructionServicesAdmin;